import { useEffect, useState } from 'react';

import { previewPaynow } from '../../../api/client';
import { renderPaynowQr } from '../../display/paynowRender';
import { PREVIEW_AMOUNT_CENTS, PaynowForm, PreviewState, isSamePaynowForm } from './types';

/** How long typing settles before a preview request is sent. */
const PREVIEW_DEBOUNCE_MS = 250;

/** A finished render, tagged with the form it was drawn from. */
type PreviewResult = { form: PaynowForm; state: PreviewState };

/**
 * Draw the live preview from the unsaved form.
 *
 * It renders through the same code path as the customer display, so the two
 * cannot disagree. Requests are debounced so typing does not fire one per
 * keystroke, and an in-flight request is aborted when the form changes again.
 *
 * The visible state is derived from the latest finished render rather than
 * stored directly: a render that does not match the current form means a
 * request is in flight (or still debouncing), which is the loading state.
 */
export function usePaynowPreview(form: PaynowForm, loading: boolean): { state: PreviewState } {
  const [result, setResult] = useState<PreviewResult | null>(null);

  useEffect(() => {
    if (loading) return undefined;
    if (form.value.trim() === '') return undefined;

    const requested = form;
    let cancelled = false;
    const controller = new AbortController();

    const timer = window.setTimeout(() => {
      previewPaynow(
        {
          type: form.kind,
          value: form.value,
          name: form.name,
          color: form.color,
          logo_id: form.logoId,
          style: form.style,
          amount_cents: PREVIEW_AMOUNT_CENTS,
        },
        controller.signal
      )
        .then(async (preview) => {
          if (cancelled) return;
          if (!preview) {
            setResult({ form: requested, state: { status: 'unavailable' } });
            return;
          }
          try {
            const url = await renderPaynowQr(preview);
            if (!cancelled) setResult({ form: requested, state: { status: 'ready', preview, url } });
          } catch {
            if (!cancelled) setResult({ form: requested, state: { status: 'error' } });
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // An abort is this hook's own cancellation, not a failure to show.
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setResult({ form: requested, state: { status: 'error' } });
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [loading, form]);

  // idle is not loading: the settings themselves have not arrived, so there is
  // no form to preview yet. loading is a preview request in flight for a form
  // that is already usable.
  const state: PreviewState = loading
    ? { status: 'idle' }
    : form.value.trim() === ''
      ? { status: 'unavailable' }
      : result && isSamePaynowForm(result.form, form)
        ? result.state
        : { status: 'loading' };

  return { state };
}
